import styles from 'css/src/components/common/ui/MultiToggle.module.scss';

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { BUTTON_SIZES, TertiaryButton } from 'js/src/components/common/ui/Button';

export enum MULTI_TOGGLE_THEMES {
    NEUTRAL = 'neutral', // white pill on grey
    PRIMARY = 'primary', // brand-coloured pill, white text
}

export enum MULTI_TOGGLE_SIZES {
    SM = 'sm',
    LG = 'lg',
}

const THEME_CLASSES: Record<MULTI_TOGGLE_THEMES, string> = {
    [MULTI_TOGGLE_THEMES.NEUTRAL]: styles['theme-neutral'],
    [MULTI_TOGGLE_THEMES.PRIMARY]: styles['theme-primary'],
};

const SIZE_CLASSES: Record<MULTI_TOGGLE_SIZES, string | undefined> = {
    [MULTI_TOGGLE_SIZES.SM]: undefined,
    [MULTI_TOGGLE_SIZES.LG]: styles['size-lg'],
};

export type TypeMultiToggleItem = {
    key: string;
    value: string;
    testId?: string;
};
type IndicatorRect = {
    left: number;
    top: number;
    width: number;
    height: number;
    borderRadius: string;
};

export type TypeMultiToggleProps = {
    items: Array<TypeMultiToggleItem>;
    onSelect: (item: string) => void;
    selectedItem?: string;
    disabled?: boolean;
    theme?: MULTI_TOGGLE_THEMES;
    size?: MULTI_TOGGLE_SIZES;
    className?: string;
    textClassName?: string;
};

const MultiToggle: React.FC<TypeMultiToggleProps> = React.memo(function MultiToggle({
    items,
    onSelect,
    selectedItem,
    disabled,
    theme = MULTI_TOGGLE_THEMES.NEUTRAL,
    size = MULTI_TOGGLE_SIZES.SM,
    className,
    textClassName,
}) {
    const toggleItems = new Set(items);
    const groupRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
    const hasPlacedIndicator = indicator !== null;

    // A single pill slides between options instead of each button
    // swapping its own background, so the selection visibly travels.
    useLayoutEffect(() => {
        const group = groupRef.current;
        if (!group) {
            return;
        }
        const placeIndicator = () => {
            const activeButton = group.querySelector<HTMLElement>('.btn.active');
            if (!activeButton) {
                setIndicator(null);
                return;
            }
            setIndicator({
                left: activeButton.offsetLeft,
                top: activeButton.offsetTop,
                width: activeButton.offsetWidth,
                height: activeButton.offsetHeight,
                borderRadius: getComputedStyle(activeButton).borderRadius,
            });
        };
        placeIndicator();
        // Not every environment has ResizeObserver (e.g. jsdom); the pill is still placed
        // on every selection change, it just won't follow later size changes there.
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const resizeObserver = new ResizeObserver(placeIndicator);
        resizeObserver.observe(group);
        return () => resizeObserver.disconnect();
    }, [selectedItem, items, theme, size]);

    const handleToggle = useCallback(
        (itemKey: string) => {
            if (disabled) {
                return;
            }
            if (itemKey !== selectedItem) {
                onSelect(itemKey);
            }
        },
        [disabled, onSelect, selectedItem],
    );

    return (
        <div className="d-flex align-items-center">
            <div
                className={classNames(
                    'd-flex align-items-center toggle-select-group btn-rounded cursor-pointer p-l5-4 position-relative',
                    styles['track'],
                    THEME_CLASSES[theme],
                    SIZE_CLASSES[size],
                    {
                        [styles['disabled']]: disabled,
                        [styles['has-indicator']]: hasPlacedIndicator,
                    },
                )}
                role="group"
                aria-label="Multi toggle"
                ref={groupRef}
            >
                {indicator && <IndicatorPill rect={indicator} />}
                {[...toggleItems].map((item, index) => {
                    return (
                        <TertiaryButton
                            textClassName={textClassName}
                            key={index}
                            text={item.value}
                            className={classNames(styles['option'], className, {
                                active: item.key === selectedItem,
                            })}
                            size={BUTTON_SIZES.SM}
                            onClick={() => handleToggle(item.key)}
                            data-testid={item.testId}
                        />
                    );
                })}
            </div>
        </div>
    );
});

// Skips the slide on the first placement so the pill doesn't fly in from
// the group's corner when the toggle mounts.
const IndicatorPill: React.FC<{ rect: IndicatorRect }> = ({ rect }) => {
    const [canAnimate, setCanAnimate] = useState(false);

    useLayoutEffect(() => {
        const frame = requestAnimationFrame(() => setCanAnimate(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <div
            aria-hidden
            className={classNames(styles['indicator'], {
                [styles['can-animate']]: canAnimate,
            })}
            style={{
                width: rect.width,
                height: rect.height,
                borderRadius: rect.borderRadius,
                transform: `translate(${rect.left}px, ${rect.top}px)`,
            }}
        />
    );
};

export default MultiToggle;
